import React, { useEffect, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
  Alert,
} from 'react-native';
import {
  Text,
  Button,
  ProgressBar,
  Chip,
  Surface,
  useTheme,
  ActivityIndicator,
  IconButton,
  TextInput,
  Portal,
  Modal,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useOfflineJourney } from '../../hooks/useOfflineJourney';
import {
  Clock,
  CheckCircle,
  Star,
  Coffee,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
  Target,
} from 'lucide-react-native';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchJourneyActivity } from '../../store/slices/journeySlice';
import { journeyApi } from '../../services/api/journeyApi';
import type { ActivityType, CompletionInput } from '@coffeeplexity/shared';

interface RouteParams {
  activity: JourneyActivity;
  journeyId: string;
  weekNumber: number;
}

export function ActivityDetailScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();
  const { completeActivityOffline, updateProgressOffline } =
    useOfflineJourney();

  const { activity: passedActivity } = route.params as RouteParams;
  // const { journeyId, weekNumber } = route.params as RouteParams; // Unused for now

  // Use the passed activity directly instead of fetching
  const [currentActivity, setCurrentActivity] =
    useState<JourneyActivity | null>(passedActivity);
  const [loading, setLoading] = useState({
    activity: false,
    completing: false,
  });

  const [refreshing, setRefreshing] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);

  useEffect(() => {
    // Set the activity from route params
    setCurrentActivity(passedActivity);
    setStartTime(new Date());
  }, [passedActivity]);

  useEffect(() => {
    // Calculate time spent when component unmounts or activity changes
    return () => {
      if (startTime) {
        const endTime = new Date();
        const timeSpentMinutes = Math.round(
          (endTime.getTime() - startTime.getTime()) / (1000 * 60)
        );
        setTimeSpent(timeSpentMinutes);
      }
    };
  }, [startTime]);

  const onRefresh = async () => {
    setRefreshing(true);
    // Since we're using passed data, just reset the refreshing state
    // In a real app, you might want to refetch the journey data
    setRefreshing(false);
  };

  const handleStartActivity = async () => {
    if (!currentActivity) return;

    // Update progress to in_progress using offline service
    await updateProgressOffline(
      currentActivity.id,
      10, // 10% progress when starting
      'current_user' // TODO: Get from auth context
    );

    setStartTime(new Date());
  };

  const handleCompleteActivity = () => {
    setShowCompletionModal(true);
  };

  const handleConfirmCompletion = async () => {
    if (!currentActivity) return;

    const completionData: CompletionInput = {
      time_spent_minutes: timeSpent || currentActivity.estimated_minutes || 0,
      success_rate: 1.0,
      ...(rating > 0 && { difficulty_rating: rating }),
      engagement_score: 0.8,
      ...(feedback && { notes: feedback }),
    };

    try {
      const success = await completeActivityOffline(
        currentActivity.id,
        completionData,
        'current_user' // TODO: Get from auth context
      );

      if (success) {
        setShowCompletionModal(false);

        // Show success message
        Alert.alert(
          'Activity Completed!',
          "Great job! You've completed this activity. Your progress will sync when you're back online.",
          [
            {
              text: 'Rate Activity',
              onPress: () => setShowRatingModal(true),
            },
            {
              text: 'Continue',
              onPress: () => navigation.goBack(),
            },
          ]
        );
      } else {
        Alert.alert('Error', 'Failed to complete activity. Please try again.');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to complete activity. Please try again.');
    }
  };

  const handleRateActivity = async () => {
    if (!currentActivity || rating === 0) return;

    try {
      await journeyApi.rateActivity(currentActivity.id, rating, feedback);
      setShowRatingModal(false);
      setRating(0);
      setFeedback('');
      navigation.goBack();
    } catch (error) {
      Alert.alert('Error', 'Failed to submit rating. Please try again.');
    }
  };

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'video':
        return Video;
      case 'recipe':
        return Coffee;
      case 'reading':
        return BookOpen;
      case 'quiz':
        return HelpCircle;
      case 'practice':
        return Target;
      default:
        return FileText;
    }
  };

  const getActivityTypeColor = (type: ActivityType) => {
    switch (type) {
      case 'video':
        return theme.colors.error;
      case 'recipe':
        return theme.colors.primary;
      case 'reading':
        return theme.colors.tertiary;
      case 'quiz':
        return theme.colors.secondary;
      case 'practice':
        return theme.colors.outline;
      default:
        return theme.colors.onSurfaceVariant;
    }
  };

  const getActivityTypeLabel = (type: ActivityType) => {
    switch (type) {
      case 'video':
        return 'Video';
      case 'recipe':
        return 'Recipe';
      case 'reading':
        return 'Reading';
      case 'quiz':
        return 'Quiz';
      case 'practice':
        return 'Practice';
      default:
        return type;
    }
  };

  if (loading.activity) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <ActivityIndicator size='large' />
        <Text style={styles.loadingText}>Loading activity...</Text>
      </View>
    );
  }

  if (!currentActivity) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <Text variant='headlineSmall'>Activity not found</Text>
        <Button onPress={() => navigation.goBack()} style={styles.backButton}>
          Go Back
        </Button>
      </View>
    );
  }

  const IconComponent = getActivityIcon(currentActivity.activity_type);
  const typeColor = getActivityTypeColor(currentActivity.activity_type);
  const typeLabel = getActivityTypeLabel(currentActivity.activity_type);

  const progressPercentage = currentActivity.progress?.progress_percentage || 0;
  const isCompleted = currentActivity.is_completed;
  const isInProgress = progressPercentage > 0 && !isCompleted;
  const isNotStarted = progressPercentage === 0 && !isCompleted;

  return (
    <>
      <ScrollView
        style={[
          styles.container,
          { paddingTop: insets.top, backgroundColor: theme.colors.background },
        ]}
        contentContainerStyle={styles.contentContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Header */}
        <View style={styles.header}>
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: theme.colors.surfaceVariant },
            ]}
          >
            <IconComponent size={32} color={typeColor} />
          </View>
          <View style={styles.headerInfo}>
            <Text variant='headlineSmall' style={styles.title}>
              {currentActivity.title}
            </Text>
            <View style={styles.headerMeta}>
              <Chip
                mode='outlined'
                textStyle={{ color: typeColor }}
                style={{ borderColor: typeColor }}
              >
                {typeLabel}
              </Chip>
              <View style={styles.metaItem}>
                <Clock size={16} color={theme.colors.onSurfaceVariant} />
                <Text variant='bodySmall' style={styles.metaText}>
                  {currentActivity.estimated_minutes} min
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Status Card */}
        <Surface style={styles.statusCard} elevation={1}>
          <View style={styles.statusHeader}>
            <Text variant='titleMedium' style={styles.statusTitle}>
              Progress
            </Text>
            {isCompleted && (
              <CheckCircle size={24} color={theme.colors.primary} />
            )}
          </View>

          {isCompleted ? (
            <View style={styles.completedStatus}>
              <Text variant='bodyMedium' style={styles.completedText}>
                ✅ Completed
              </Text>
              {currentActivity.completion_date && (
                <Text variant='bodySmall' style={styles.completionDate}>
                  Completed on{' '}
                  {new Date(
                    currentActivity.completion_date
                  ).toLocaleDateString()}
                </Text>
              )}
            </View>
          ) : (
            <>
              <ProgressBar
                progress={progressPercentage / 100}
                color={theme.colors.primary}
                style={styles.progressBar}
              />
              <Text variant='bodySmall' style={styles.progressText}>
                {Math.round(progressPercentage)}% complete
              </Text>
            </>
          )}
        </Surface>

        {/* Description */}
        {currentActivity.description && (
          <Surface style={styles.descriptionCard} elevation={1}>
            <Text variant='titleMedium' style={styles.descriptionTitle}>
              Description
            </Text>
            <Text variant='bodyMedium' style={styles.descriptionText}>
              {currentActivity.description}
            </Text>
          </Surface>
        )}

        {/* Content */}
        {currentActivity.content && (
          <Surface style={styles.contentCard} elevation={1}>
            <Text variant='titleMedium' style={styles.contentTitle}>
              Content
            </Text>
            <View style={styles.contentInfo}>
              <Text variant='bodyMedium' style={styles.contentName}>
                {currentActivity.content.title}
              </Text>
              {currentActivity.content.author && (
                <Text variant='bodySmall' style={styles.contentAuthor}>
                  by {currentActivity.content.author}
                </Text>
              )}
              {currentActivity.content.duration_minutes && (
                <View style={styles.metaItem}>
                  <Clock size={14} color={theme.colors.onSurfaceVariant} />
                  <Text variant='bodySmall' style={styles.metaText}>
                    {currentActivity.content.duration_minutes} min
                  </Text>
                </View>
              )}
            </View>
            {currentActivity.content.url && (
              <Button
                mode='outlined'
                onPress={() => {
                  /* TODO: Open content URL */
                }}
                style={styles.contentButton}
                icon='external-link'
              >
                Open Content
              </Button>
            )}
          </Surface>
        )}

        {/* Required Equipment */}
        {currentActivity.required_equipment.length > 0 && (
          <Surface style={styles.equipmentCard} elevation={1}>
            <Text variant='titleMedium' style={styles.equipmentTitle}>
              Required Equipment
            </Text>
            <View style={styles.equipmentList}>
              {currentActivity.required_equipment.map((equipment, index) => (
                <Chip key={index} mode='outlined' style={styles.equipmentChip}>
                  {equipment}
                </Chip>
              ))}
            </View>
          </Surface>
        )}

        {/* Difficulty */}
        <Surface style={styles.difficultyCard} elevation={1}>
          <View style={styles.difficultyHeader}>
            <Text variant='titleMedium' style={styles.difficultyTitle}>
              Difficulty Level
            </Text>
            <View style={styles.difficultyIndicator}>
              {[1, 2, 3, 4, 5].map(level => (
                <View
                  key={level}
                  style={[
                    styles.difficultyDot,
                    {
                      backgroundColor:
                        level <= currentActivity.difficulty_level * 5
                          ? theme.colors.primary
                          : theme.colors.outline,
                    },
                  ]}
                />
              ))}
            </View>
          </View>
        </Surface>

        {/* Previous Rating */}
        {currentActivity.user_rating && (
          <Surface style={styles.ratingCard} elevation={1}>
            <Text variant='titleMedium' style={styles.ratingTitle}>
              Your Rating
            </Text>
            <View style={styles.ratingDisplay}>
              <View style={styles.stars}>
                {[1, 2, 3, 4, 5].map(star => (
                  <Star
                    key={star}
                    size={20}
                    color={
                      star <= currentActivity.user_rating!
                        ? theme.colors.primary
                        : theme.colors.outline
                    }
                    fill={
                      star <= currentActivity.user_rating!
                        ? theme.colors.primary
                        : 'transparent'
                    }
                  />
                ))}
              </View>
              <Text variant='bodyMedium'>
                {currentActivity.user_rating}/5 stars
              </Text>
            </View>
            {currentActivity.user_feedback && (
              <Text variant='bodySmall' style={styles.feedbackText}>
                "{currentActivity.user_feedback}"
              </Text>
            )}
          </Surface>
        )}
      </ScrollView>

      {/* Action Button */}
      <Surface
        style={[styles.actionContainer, { paddingBottom: insets.bottom }]}
        elevation={8}
      >
        {isNotStarted && (
          <Button
            mode='contained'
            onPress={handleStartActivity}
            style={styles.actionButton}
            icon='play'
          >
            Start Activity
          </Button>
        )}
        {isInProgress && (
          <Button
            mode='contained'
            onPress={handleCompleteActivity}
            style={styles.actionButton}
            icon='check'
          >
            Mark Complete
          </Button>
        )}
        {isCompleted && !currentActivity.user_rating && (
          <Button
            mode='outlined'
            onPress={() => setShowRatingModal(true)}
            style={styles.actionButton}
            icon='star'
          >
            Rate Activity
          </Button>
        )}
      </Surface>

      {/* Completion Modal */}
      <Portal>
        <Modal
          visible={showCompletionModal}
          onDismiss={() => setShowCompletionModal(false)}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant='titleLarge' style={styles.modalTitle}>
            Complete Activity
          </Text>
          <Text variant='bodyMedium' style={styles.modalText}>
            Are you sure you want to mark this activity as complete?
          </Text>
          <View style={styles.modalActions}>
            <Button
              mode='outlined'
              onPress={() => setShowCompletionModal(false)}
              style={styles.modalButton}
            >
              Cancel
            </Button>
            <Button
              mode='contained'
              onPress={handleConfirmCompletion}
              style={styles.modalButton}
              loading={loading.completing}
            >
              Complete
            </Button>
          </View>
        </Modal>
      </Portal>

      {/* Rating Modal */}
      <Portal>
        <Modal
          visible={showRatingModal}
          onDismiss={() => setShowRatingModal(false)}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant='titleLarge' style={styles.modalTitle}>
            Rate This Activity
          </Text>
          <Text variant='bodyMedium' style={styles.modalText}>
            How would you rate this activity?
          </Text>

          <View style={styles.ratingInput}>
            <View style={styles.stars}>
              {[1, 2, 3, 4, 5].map(star => (
                <IconButton
                  key={star}
                  icon='star'
                  iconColor={
                    star <= rating ? theme.colors.primary : theme.colors.outline
                  }
                  onPress={() => setRating(star)}
                />
              ))}
            </View>
          </View>

          <TextInput
            label='Feedback (optional)'
            value={feedback}
            onChangeText={setFeedback}
            multiline
            numberOfLines={3}
            style={styles.feedbackInput}
          />

          <View style={styles.modalActions}>
            <Button
              mode='outlined'
              onPress={() => {
                setShowRatingModal(false);
                setRating(0);
                setFeedback('');
              }}
              style={styles.modalButton}
            >
              Cancel
            </Button>
            <Button
              mode='contained'
              onPress={handleRateActivity}
              style={styles.modalButton}
              disabled={rating === 0}
            >
              Submit Rating
            </Button>
          </View>
        </Modal>
      </Portal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 100, // Space for action button
  },
  backButton: {
    alignSelf: 'flex-start',
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  oldBackButton: {
    marginTop: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 12,
    lineHeight: 28,
  },
  headerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    marginLeft: 4,
    opacity: 0.7,
  },
  statusCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  statusHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  statusTitle: {
    fontWeight: 'bold',
  },
  completedStatus: {
    alignItems: 'center',
  },
  completedText: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  completionDate: {
    opacity: 0.7,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: 8,
  },
  progressText: {
    opacity: 0.7,
  },
  descriptionCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  descriptionTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  descriptionText: {
    lineHeight: 22,
    opacity: 0.8,
  },
  contentCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  contentTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  contentInfo: {
    marginBottom: 16,
  },
  contentName: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  contentAuthor: {
    opacity: 0.7,
    marginBottom: 8,
  },
  contentButton: {
    alignSelf: 'flex-start',
  },
  equipmentCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  equipmentTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  equipmentList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  equipmentChip: {
    marginRight: 4,
    marginBottom: 4,
  },
  difficultyCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  difficultyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  difficultyTitle: {
    fontWeight: 'bold',
  },
  difficultyIndicator: {
    flexDirection: 'row',
    gap: 6,
  },
  difficultyDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  ratingCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  ratingTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  ratingDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  stars: {
    flexDirection: 'row',
    gap: 4,
  },
  feedbackText: {
    opacity: 0.7,
    fontStyle: 'italic',
  },
  actionContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  actionButton: {
    width: '100%',
  },
  modalContainer: {
    padding: 24,
    margin: 20,
    borderRadius: 12,
  },
  modalTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  modalText: {
    marginBottom: 24,
    opacity: 0.7,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  modalButton: {
    minWidth: 80,
  },
  ratingInput: {
    alignItems: 'center',
    marginBottom: 16,
  },
  feedbackInput: {
    marginBottom: 24,
  },
});
